import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { StepCircle, type StepState } from "@/components/ui/step-circle";
import { cn } from "@/lib/utils";

/**
 * CHP-808: `step` is constrained to `totalSteps` via a discriminated union so an
 * inconsistent pair like (totalSteps=2, step=3) — which would render a current
 * marker for a step outside the 2-column strip — is unrepresentable. `totalSteps`
 * defaults to 3, so an omitted `totalSteps` admits step 1|2|3 (the 3-step path).
 *
 * Total steps the user will walk. The org's signing PO gets the full 3-step
 * Flow A (Identity → SPIF → T&C). Admins/managers skip SPIF per the spec and
 * see 2 steps (Identity → T&C). CHP-1021: a non-signing partner (a BDM, or an
 * additional PO of an already-registered org) inherits the org's rewards
 * preference and T&C acceptance, so Identity is their ONLY step (totalSteps=1) —
 * the progress strip is hidden in that case (a one-of-one bar is noise). Drives
 * both the progress strip's column count and the label shown for step 2
 * (Rewards Model for 3-step, Terms for 2-step).
 */
type OnboardingStepProps =
  { totalSteps?: 3; step: 1 | 2 | 3 } | { totalSteps: 2; step: 1 | 2 } | { totalSteps: 1; step: 1 };

type OnboardingShellProps = OnboardingStepProps & {
  title: string;
  children: ReactNode;
};

/**
 * Chrome for the post-invite onboarding wizard (Account Creation slices 1
 * + 1a). Mounted OUTSIDE ProtectedRoute because the user has
 * `tc_accepted_at == null` until the final step writes it.
 *
 * `totalSteps=3` (default): Identity → SPIF → T&C. Channel partners only.
 * `totalSteps=2`: Identity → T&C. Admins/managers — SPIF is skipped per
 * the Confluence "FOR REVIEW Account Creation" Flow A spec (SPIF is a
 * partner-org preference, meaningless for internal roles).
 */
export function OnboardingShell({ step, totalSteps = 3, title, children }: OnboardingShellProps) {
  // Build the labels array from totalSteps so the strip stays consistent
  // with the route the user will actually visit next. Static record keyed
  // on absolute step number would mislabel step 2 in the 2-step path.
  const steps =
    totalSteps === 3
      ? [
          { n: 1, label: "Your details", to: "/onboarding/identity" },
          { n: 2, label: "Rewards Model", to: "/onboarding/spif" },
          { n: 3, label: "Terms", to: "/onboarding/terms" },
        ]
      : totalSteps === 2
        ? [
            { n: 1, label: "Your details", to: "/onboarding/identity" },
            { n: 2, label: "Terms", to: "/onboarding/terms" },
          ]
        : [{ n: 1, label: "Your details", to: "/onboarding/identity" }];

  return (
    <AuthShell
      headingId="onboarding-heading"
      showLogo
      overline="Welcome"
      overlineClassName="text-sm font-semibold uppercase tracking-[0.18em] text-primary"
      heading={title}
      maxWidthClassName="max-w-lg"
      wrapperClassName="min-h-screen flex items-center justify-center px-4 py-10"
    >
      {/* CHP-1021: a non-signing partner walks a single Identity step, so the
          progress strip is suppressed — a one-of-one bar reads as noise and
          implies steps that don't exist. */}
      {totalSteps > 1 && (
        <ol
          aria-label="Progress"
          className={cn(
            "grid gap-1 text-xs font-medium",
            totalSteps === 3 ? "grid-cols-3" : "grid-cols-2",
          )}
        >
          {steps.map(({ n, label, to }) => {
            // CHP-500: circled step number (shared StepCircle), matching the
            // deal-registration wizard. Done = check, current = filled, upcoming
            // = outlined neutral.
            const state: StepState = n < step ? "done" : n === step ? "current" : "upcoming";
            return (
              <li key={n} aria-current={n === step ? "step" : undefined}>
                {/* Completed steps are links so the user can go back and edit
                  (design review CHP-293); the current and future steps stay
                  non-interactive. */}
                {n < step ? (
                  <Link
                    to={to}
                    className="flex items-center justify-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-center text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
                  >
                    <StepCircle n={n} state={state} />
                    <span className="sr-only">Completed: </span>
                    {label}
                  </Link>
                ) : (
                  <span
                    className={cn(
                      "flex items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-center transition-colors",
                      n === step
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border/40 text-muted-foreground/70",
                    )}
                  >
                    <StepCircle n={n} state={state} />
                    {n === step ? <span className="sr-only">Current step: </span> : null}
                    {label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <div className="space-y-4">{children}</div>
    </AuthShell>
  );
}

// CHP-313: previous-step route for the wizard's Back control. Mirrors the
// `steps` array in OnboardingShell; null on step 1 (nothing to go back to).
// CHP-808: take the same discriminated (step, totalSteps) pair the shell uses,
// so an out-of-strip step (e.g. step 3 in the 2-step path) can't be passed.
export function onboardingPreviousRoute(arg: OnboardingStepProps): string | null {
  const { step, totalSteps = 3 } = arg;
  if (step <= 1) return null;
  const routes =
    totalSteps === 3
      ? ["/onboarding/identity", "/onboarding/spif", "/onboarding/terms"]
      : ["/onboarding/identity", "/onboarding/terms"];
  return routes[step - 2] ?? null;
}

/**
 * The wizard's explicit Back control (CHP-313). Idiomatic placement is a footer
 * action row paired with the step's primary CTA — Back (secondary) on the left,
 * primary on the right — not a standalone link in the header chrome. Renders
 * nothing on step 1. `asChild` keeps it a real link (with href) styled as a
 * secondary button so it sits flush beside the primary.
 */
export function OnboardingBackButton(props: OnboardingStepProps) {
  const to = onboardingPreviousRoute(props);
  if (to === null) return null;
  return (
    <Button asChild variant="outline" size="lg">
      <Link to={to}>
        <span aria-hidden>←</span> Back
      </Link>
    </Button>
  );
}
